import React, { useEffect, useState } from 'react';
import { GlobeAltIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';

const TimezoneSelector = ({ value, onChange, showLabel = true, showDetectedOption = true }) => {
  const { t } = useLanguage();
  const [detectedTimezone, setDetectedTimezone] = useState('');
  
  // Liste des fuseaux horaires courants
  const timezones = [
    { value: 'Europe/Paris', label: 'Paris (UTC+1/+2)' },
    { value: 'Europe/London', label: 'Londres (UTC+0/+1)' },
    { value: 'Europe/Berlin', label: 'Berlin (UTC+1/+2)' },
    { value: 'Europe/Madrid', label: 'Madrid (UTC+1/+2)' },
    { value: 'America/New_York', label: 'New York (UTC-5/-4)' },
    { value: 'America/Montreal', label: 'Montréal/Québec (UTC-5/-4)' },
    { value: 'America/Toronto', label: 'Toronto (UTC-5/-4)' },
    { value: 'America/Los_Angeles', label: 'Los Angeles (UTC-8/-7)' },
    { value: 'America/Chicago', label: 'Chicago (UTC-6/-5)' },
    { value: 'America/Mexico_City', label: 'Mexico (UTC-6/-5)' },
    { value: 'America/Sao_Paulo', label: 'São Paulo (UTC-3)' },
    { value: 'Asia/Tokyo', label: 'Tokyo (UTC+9)' },
    { value: 'Asia/Shanghai', label: 'Shanghai (UTC+8)' },
    { value: 'Asia/Dubai', label: 'Dubai (UTC+4)' },
    { value: 'Asia/Singapore', label: 'Singapour (UTC+8)' },
    { value: 'Australia/Sydney', label: 'Sydney (UTC+10/+11)' },
    { value: 'Pacific/Auckland', label: 'Auckland (UTC+12/+13)' },
    { value: 'Africa/Johannesburg', label: 'Johannesburg (UTC+2)' },
    { value: 'Africa/Cairo', label: 'Le Caire (UTC+2)' },
    { value: 'UTC', label: 'UTC' }
  ];
  
  useEffect(() => {
    // Détecter le fuseau horaire du navigateur
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      setDetectedTimezone(timezone);
      if (!value && onChange) {
        onChange(timezone);
      }
    } catch (error) {
      console.error('Error detecting timezone:', error);
      // Fallback to UTC
      if (!value && onChange) {
        onChange('UTC');
      }
    }
  }, [onChange, value]);
  
  // Ajouter le fuseau détecté s'il n'est pas dans la liste
  const allTimezones = [...timezones];
  if (detectedTimezone && !timezones.find(tz => tz.value === detectedTimezone)) {
    allTimezones.unshift({
      value: detectedTimezone,
      label: `${detectedTimezone} (${t('appointments.timezone.detected', 'Détecté')})`
    });
  }
  
  return (
    <div className="space-y-2">
      {showLabel && (
        <label className="flex items-center text-sm font-medium text-gray-700">
          <GlobeAltIcon className="h-4 w-4 mr-2" />
          {t('appointments.timezone.label', 'Fuseau horaire')}
        </label>
      )}
      
      <select
        value={value || detectedTimezone || 'UTC'}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
      >
        {allTimezones.map(tz => (
          <option key={tz.value} value={tz.value}>
            {tz.label}
          </option>
        ))}
      </select>
      
      {showDetectedOption && detectedTimezone && value !== detectedTimezone && (
        <button
          type="button"
          onClick={() => onChange(detectedTimezone)}
          className="text-xs text-primary-600 hover:text-primary-700"
          aria-label={t('appointments.settings.useDetected')}
        >
          {t('appointments.settings.useDetected', 'Use your detected timezone')} ({detectedTimezone})
        </button>
      )}
    </div>
  );
};

export default TimezoneSelector;
